/** Account deletion: the pieces that are pure, so they can be tested without
 *  a database, a Stripe key, or a live subscription.
 *
 *  Apple Guideline 5.1.1(v) requires an in-app deletion path for any app that
 *  supports account creation, which is what this backs. */

/** Subscription statuses where Stripe still moves money, mirrored EXACTLY from
 *  `prevent_delete_profile_with_live_billing()` — first issued in migration
 *  20260817022419_billing_subscriptions_user_delete_cascade.sql, re-issued
 *  with the `provider = 'stripe'` scope in 20260906205847.
 *
 *  Duplicated deliberately, and pinned by a test that reads the LATEST
 *  migration re-issuing the function (forward-only migrations mean each
 *  reissue is a new file). The DB trigger is the real gate (it fires on the cascade no matter
 *  who issues the DELETE); this list is how the app finds the same rows in
 *  order to CANCEL them first. Drift in either direction is a bug: cancel too
 *  little and the delete is refused, cancel too much and we close a
 *  subscription the DB would have allowed to survive. */
export const LIVE_BILLING_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "unpaid",
  "paused",
] as const;

export const DELETE_CONFIRM_PHRASE = "DELETE";

/** Unique Stripe subscription ids to cancel before deleting the account.
 *
 *  Deduped because `billing_subscriptions` is a projection keyed per row, and
 *  a resubscribe can leave two rows pointing at one Stripe subscription;
 *  cancelling the same id twice is wasted work and a confusing log line. */
export function subscriptionIdsToCancel(
  rows: readonly { stripe_subscription_id: string | null }[],
): string[] {
  const ids = rows
    .map((row) => row.stripe_subscription_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
  return [...new Set(ids)];
}

/** Recognise the DB guard trigger firing anyway.
 *
 *  We cancel live subscriptions before deleting, but the cancel and the DELETE
 *  are not one transaction: a webhook can project a fresh `active` row in
 *  between, and the projection is also what a resubscribe writes. Rare, but
 *  the difference between a useful message and a generic failure.
 *
 *  Matches the MESSAGE, not the code. Verified against a real local delete,
 *  GoTrue passes the Postgres error through nearly intact:
 *    {"code":"23001","message":"Cannot delete user <id>: Stripe subscription
 *     <sub> is still active","hint":"Cancel or refund ..."}
 *  So `23001` (restrict_violation) is available and would be the tempting
 *  match, but it is the generic code for ANY restrict FK, and
 *  `billing_subscriptions.organization_id` deliberately still carries
 *  `on delete restrict`. The message is what actually identifies THIS guard. */
export function isLiveBillingRejection(message: string | undefined): boolean {
  if (!message) return false;
  return /Cannot delete user .* is still/i.test(message);
}
